import { isWorkbenchNonRerunRun } from '../../../utils/workbenchRunFilter';

export type PDCRun = Record<string, unknown>;

export type PDCScoreData = {
  riskScore: number;
  defenseScore: number;
  riskTier: string;
  datasetStatus: string;
  verdict: string;
  attackCoverage: number;
  detectionScore: number;
  injectionScore: number;
  jailbreakScore: number;
  leakageScore: number;
  exfiltrationScore: number;
  obfuscationScore: number;
  outputRiskScore: number;
  explainabilityScore: number;
  businessImpactScore: number;
  complianceImpactScore: number;
  operationalImpactScore: number;
  dataExposureRiskScore: number;
  totalFindings: number;
  findingsVerdictScore: number;
  findingsVerdictStatus: string;
  findingsRecommendation: string;
};

export type PDCFindingEvidence = {
  finding_id?: string;
  source?: string;
  attack_type?: string;
  component?: string;
  owasp_mapping?: string;
  mitre_mapping?: string;
  risk_level?: string;
  confidence?: number;
  evidence_text?: string;
  evidence?: string[];
  root_cause?: { description?: string; category?: string };
  remediation_steps?: string[];
  impact?: Record<string, string>;
  [key: string]: unknown;
};

/** True when run uses PDC+ schema (not legacy LLM/ML_PROMPT_INJECTION_ANALYSIS). */
export function isPDCRun(run: unknown): run is PDCRun {
  if (!run || typeof run !== 'object') return false;
  const r = run as Record<string, unknown>;
  if (r.summary && typeof r.summary === 'object') return true;
  const pi = r.prompt_injection;
  if (pi && typeof pi === 'object' && !Array.isArray(pi)) {
    const block = pi as Record<string, unknown>;
    return Boolean(block.prompt_defense_core || block.pdc_findings || block.pdx_explainability);
  }
  return false;
}

function readNested(root: unknown, path: string[]): unknown {
  let cur: unknown = root;
  for (const p of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

export const getPDCScoreData = (run: PDCRun | null | undefined): PDCScoreData | null => {
  if (!run) return null;
  const summary =
    run.summary && typeof run.summary === 'object' && !Array.isArray(run.summary)
      ? (run.summary as Record<string, unknown>)
      : undefined;

  return {
    riskScore: Number(summary?.risk_score ?? 0) || 0,
    defenseScore: Number(summary?.defense_score ?? 0) || 0,
    riskTier: String(summary?.risk_tier ?? 'Low'),
    datasetStatus: String(summary?.dataset_status ?? 'N/A'),
    verdict: String(summary?.dataset_verdict ?? 'APPROVE'),
    attackCoverage: Number(summary?.attack_coverage ?? 0) || 0,

    detectionScore:
      Number(readNested(run, ['prompt_injection', 'prompt_defense_core', 'kpis', 'detection_score']) ?? 0) || 0,
    injectionScore:
      Number(readNested(run, ['prompt_injection', 'prompt_defense_core', 'kpis', 'injection_score']) ?? 0) || 0,
    jailbreakScore:
      Number(readNested(run, ['prompt_injection', 'prompt_defense_core', 'kpis', 'jailbreak_score']) ?? 0) || 0,
    leakageScore:
      Number(readNested(run, ['prompt_injection', 'prompt_defense_core', 'kpis', 'leakage_score']) ?? 0) || 0,
    exfiltrationScore:
      Number(readNested(run, ['prompt_injection', 'prompt_defense_core', 'kpis', 'exfiltration_score']) ?? 0) || 0,
    obfuscationScore:
      Number(readNested(run, ['prompt_injection', 'prompt_defense_core', 'kpis', 'obfuscation_score']) ?? 0) || 0,
    outputRiskScore:
      Number(readNested(run, ['prompt_injection', 'prompt_defense_core', 'kpis', 'output_risk_score']) ?? 0) || 0,

    explainabilityScore:
      Number(readNested(run, ['prompt_injection', 'pdx_explainability', 'kpis', 'explainability_score']) ?? 0) || 0,

    businessImpactScore:
      Number(readNested(run, ['prompt_injection', 'pdc_abr_blast_radius', 'kpis', 'business_impact_score']) ?? 0) || 0,
    complianceImpactScore:
      Number(readNested(run, ['prompt_injection', 'pdc_abr_blast_radius', 'kpis', 'compliance_impact_score']) ?? 0) ||
      0,
    operationalImpactScore:
      Number(readNested(run, ['prompt_injection', 'pdc_abr_blast_radius', 'kpis', 'operational_impact_score']) ?? 0) ||
      0,
    dataExposureRiskScore:
      Number(readNested(run, ['prompt_injection', 'pdc_abr_blast_radius', 'kpis', 'data_exposure_risk_score']) ?? 0) ||
      0,

    totalFindings: Number(readNested(run, ['prompt_injection', 'pdc_findings', 'kpis', 'total_findings']) ?? 0) || 0,
    findingsVerdictScore: Number(readNested(run, ['prompt_injection', 'pdc_findings', 'verdict', 'score']) ?? 0) || 0,
    findingsVerdictStatus: String(
      readNested(run, ['prompt_injection', 'pdc_findings', 'verdict', 'status']) ?? 'Passed'
    ),
    findingsRecommendation: String(
      readNested(run, ['prompt_injection', 'pdc_findings', 'verdict', 'recommendation']) ?? ''
    )
  };
};

export function readPDCFindingsEvidence(run: PDCRun | null | undefined): PDCFindingEvidence[] {
  const raw = readNested(run, ['prompt_injection', 'pdc_findings', 'evidence']);
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is PDCFindingEvidence => row != null && typeof row === 'object');
}

export function readPDCExplainabilityEvidence(run: PDCRun | null | undefined): Array<Record<string, unknown>> {
  const raw = readNested(run, ['prompt_injection', 'pdx_explainability', 'evidence']);
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is Record<string, unknown> => row != null && typeof row === 'object');
}

export function readPDCFindingsKpis(run: PDCRun | null | undefined): Record<string, unknown> {
  const raw = readNested(run, ['prompt_injection', 'pdc_findings', 'kpis']);
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
}

export function countByKey<T extends Record<string, unknown>>(
  items: T[],
  keyFn: (item: T) => string | undefined
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of items) {
    const key = keyFn(item)?.trim();
    if (!key) continue;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Any non-rerun row in the prompt_injection bucket counts as a version (Bias pattern). */
function isPromptInjectionBucketRun(item: unknown): item is PDCRun {
  return isRecord(item) && isWorkbenchNonRerunRun(item);
}

function normalizeTimestamp(ts: unknown): number {
  if (ts == null) return 0;
  if (typeof ts === 'number' && !Number.isNaN(ts)) return ts;
  if (typeof ts === 'string') {
    const n = Date.parse(ts);
    return Number.isNaN(n) ? 0 : n;
  }
  if (typeof ts === 'object' && ts !== null && '$date' in (ts as Record<string, unknown>)) {
    const d = (ts as { $date?: unknown }).$date;
    if (typeof d === 'number') return d;
    if (typeof d === 'string') {
      const n = Date.parse(d);
      return Number.isNaN(n) ? 0 : n;
    }
  }
  return 0;
}

export function formatPDCVersionTimestamp(ts: unknown): string {
  if (!ts) return '';
  if (typeof ts === 'object' && ts !== null && '$date' in (ts as Record<string, unknown>)) {
    const d = (ts as { $date?: unknown }).$date;
    if (typeof d === 'number') return new Date(d).toISOString();
    if (typeof d === 'string') return d;
  }
  if (typeof ts === 'string') return ts;
  if (typeof ts === 'number' && Number.isFinite(ts)) return new Date(ts).toISOString();
  return '';
}

export function createVersionId(run: PDCRun, fallbackIndex = 0): string {
  const rid = String(run.result_id ?? '').trim();
  if (rid && rid !== '[object Object]') return rid;
  const title = String(run.title ?? 'pdc').trim() || 'pdc';
  const ts = formatPDCVersionTimestamp(run.timestamp) || String(fallbackIndex);
  return `${title}-${ts}`;
}

function runDedupeKey(run: PDCRun, fallbackIndex: number): string {
  const rid = String(run.result_id ?? '').trim();
  if (rid && rid !== '[object Object]') return `id:${rid}`;
  const title = String(run.title ?? '').trim();
  const ts = normalizeTimestamp(run.timestamp);
  if (title || ts) return `meta:${title}:${ts}`;
  return `idx:${fallbackIndex}`;
}

const pushRun = (candidate: unknown, versions: PDCRun[], seen: Set<string>) => {
  if (!candidate) return;

  const pushOne = (item: unknown, index: number) => {
    if (!isPromptInjectionBucketRun(item)) return;
    const key = runDedupeKey(item, index);
    if (seen.has(key)) return;
    seen.add(key);
    const id = createVersionId(item, index);
    versions.push({ ...item, result_id: item.result_id ?? id });
  };

  if (Array.isArray(candidate)) {
    candidate.forEach((item, index) => pushOne(item, index));
    return;
  }
  pushOne(candidate, seen.size);
};

function gatherPDCRunsFromProject(projectResults: unknown): PDCRun[] {
  const versions: PDCRun[] = [];
  const seen = new Set<string>();
  if (!projectResults || typeof projectResults !== 'object') return versions;

  const pr = projectResults as Record<string, unknown>;
  const results = pr.results as Record<string, unknown> | undefined;
  const collectionResults = pr.collection_results as Record<string, unknown> | undefined;
  const featureResults = pr.feature_results as Record<string, unknown> | undefined;
  const combinedResults = (pr.combined_llm_analysis as Record<string, unknown> | undefined)?.results as
    | Record<string, unknown>
    | undefined;

  // Prefer collection_results (full history), then results / feature_results.
  pushRun(collectionResults?.prompt_injection, versions, seen);
  pushRun(results?.prompt_injection, versions, seen);
  pushRun(featureResults?.prompt_injection, versions, seen);
  pushRun(combinedResults?.prompt_injection, versions, seen);
  pushRun(pr.prompt_injection, versions, seen);

  return versions;
}

function readUploadVersionEntries(run: PDCRun): Record<string, unknown>[] {
  for (const key of ['llm_entries', 'entries'] as const) {
    const raw = run[key];
    if (!Array.isArray(raw) || raw.length <= 1) continue;
    const records = raw.filter(isRecord);
    const titled = records.filter(row => String(row.title ?? row.tag ?? row.file_name ?? '').trim());
    // Expand only when rows look like per-upload versions (not findings / attributes).
    if (titled.length > 1) return titled;
  }
  return [];
}

function mergePDCRunWithEntry(run: PDCRun, entry: Record<string, unknown>, index: number): PDCRun {
  const parentId = createVersionId(run, index);
  const entryId = String(entry.result_id ?? entry._id ?? '').trim();
  const title =
    String(entry.title ?? entry.tag ?? entry.file_name ?? '').trim() ||
    String(run.title ?? '').trim() ||
    `LLM Run ${index + 1}`;
  const timestamp = entry.timestamp ?? run.timestamp;
  const result_id = entryId || `${parentId}-entry-${index}`;

  // Entry may carry its own PDC payload; otherwise inherit parent scores.
  if (entry.summary != null || entry.prompt_injection != null) {
    return { ...run, ...entry, title, result_id, timestamp };
  }

  return { ...run, title, result_id, timestamp };
}

/** Expand runs that carry per-upload rows (AI Safety llm_entries pattern). */
export function expandPDCRunsByEntries(runs: PDCRun[]): PDCRun[] {
  return runs.flatMap(run => {
    const entries = readUploadVersionEntries(run);
    if (entries.length === 0) return [run];
    return entries.map((entry, index) => mergePDCRunWithEntry(run, entry, index));
  });
}

function asUploadArray(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.filter(isRecord);
  if (isRecord(value)) return [value];
  return [];
}

function readUploadTitle(record: Record<string, unknown>): string {
  return String(record.title ?? record.tag ?? record.file_name ?? record.name ?? '').trim();
}

/** LLM upload titles from project uploads (Copilot / email analysis / model files). */
export function buildPDCLlmUploadTitles(uploads: unknown): string[] {
  if (!uploads || typeof uploads !== 'object') return [];
  const root = uploads as Record<string, unknown>;
  const titles: string[] = [];
  const seen = new Set<string>();

  for (const record of [
    ...asUploadArray(root.llm_models),
    ...asUploadArray(root.llm_model),
    ...asUploadArray(root.LLM_MODEL)
  ]) {
    const title = readUploadTitle(record);
    if (!title) continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    titles.push(title);
  }

  return titles;
}

/**
 * When backend stores one PDC run for multiple LLM uploads, split into one version per upload
 * title (same scores) — mirrors AI Safety entry expansion for version history.
 */
export function expandPDCRunsFromUploadTitles(runs: PDCRun[], uploadTitles: string[]): PDCRun[] {
  if (runs.length === 0 || uploadTitles.length <= 1) return runs;
  if (runs.length >= uploadTitles.length) return runs;

  // Prefer expanding when we have a single shared run and multiple LLM uploads.
  if (runs.length !== 1) return runs;

  const parent = runs[0];
  const parentId = createVersionId(parent, 0);
  return uploadTitles.map((title, index) => ({
    ...parent,
    title,
    result_id: `${parentId}-upload-${index}`
  }));
}

/** Collect PDC+ runs from project results and optional custom/360 payload (AI Safety pattern). */
export function collectPDCRuns(projectResults: unknown, customData?: unknown, projectUploads?: unknown): PDCRun[] {
  const fromProject = gatherPDCRunsFromProject(projectResults);

  let fromCustom: PDCRun[] = [];
  if (customData != null) {
    if (Array.isArray(customData)) {
      fromCustom = customData.filter(isPromptInjectionBucketRun);
    } else if (isPromptInjectionBucketRun(customData)) {
      fromCustom = [customData];
    }
  }

  const versions: PDCRun[] = [];
  const seen = new Set<string>();
  const source = fromCustom.length > 0 ? [...fromProject, ...fromCustom] : fromProject;
  source.forEach(run => pushRun(run, versions, seen));

  const expandedEntries = expandPDCRunsByEntries(versions);
  const uploadTitles = buildPDCLlmUploadTitles(projectUploads);
  return expandPDCRunsFromUploadTitles(expandedEntries, uploadTitles);
}

export function sortPDCRunsNewestFirst(runs: PDCRun[]): PDCRun[] {
  return [...runs].sort((a, b) => normalizeTimestamp(b.timestamp) - normalizeTimestamp(a.timestamp));
}

export function getLatestPDCRun(runs: PDCRun[]): PDCRun | null {
  const sorted = sortPDCRunsNewestFirst(runs);
  return sorted.length > 0 ? sorted[0] : null;
}

export function getLatestPDCRunFromProjectResults(projectResults: unknown): PDCRun | null {
  return getLatestPDCRun(collectPDCRuns(projectResults));
}

/** 360 API: prefer aggregated_results.prompt_injection (PDC object). */
export function resolve360PDCRun(analysis360: unknown): PDCRun | null {
  if (!analysis360 || typeof analysis360 !== 'object') return null;
  const aggregated = (analysis360 as Record<string, unknown>).aggregated_results;
  if (!aggregated || typeof aggregated !== 'object' || Array.isArray(aggregated)) return null;

  const agg = aggregated as Record<string, unknown>;
  if (isPromptInjectionBucketRun(agg.prompt_injection)) return agg.prompt_injection;

  const detailed = agg.detailed_360_data;
  if (detailed && typeof detailed === 'object' && !Array.isArray(detailed)) {
    const pi = (detailed as Record<string, unknown>).prompt_injection;
    if (isPromptInjectionBucketRun(pi)) return pi;
  }

  return null;
}

export function matchesPDCRunId(run: PDCRun, id: string): boolean {
  if (!id) return false;
  const rid = String(run.result_id ?? '').trim();
  if (rid && rid === id) return true;
  return createVersionId(run, 0) === id;
}

export function formatPDCVersionTitle(run: PDCRun, index: number): string {
  const title = String(run.title ?? '').trim();
  if (title) return title;
  return `LLM Run ${index + 1}`;
}
